/* eslint-disable no-nested-ternary */
import { useRef, useState } from "react";
import styles from "./TrustPilotBanner.module.scss";
import LazyImage from "@Components/Image";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import { getRandomAvatar } from "@Pages/PropertyPage/components/ReviewsSectionV2/avatars";
import CountryFlag from "@Components/CountryFlag";
import { getNationalityCountryCode } from "@Pages/PropertyPage/components/ReviewsSectionV2/utils";

type TPCarouselItem = {
  tp: any;
  isHomePage?: boolean;
  isDesktop?: boolean;
  hideFlagBadge?: boolean;
};

const TPCarouselItem = ({
  tp,
  isHomePage = false,
  isDesktop = false,
  hideFlagBadge = false,
}: TPCarouselItem) => {
  const { t } = useTranslationHook();
  const userLanguage = useIsLanguage();
  const charLimit = 120;
  const ref = useRef<any>();
  const [expanded, setExpanded] = useState(false);

  const reviewText = config.IS_CHINA_APP
    ? tp?.edited_review_zh || tp?.edited_review || ""
    : tp?.[`edited_review_${userLanguage}`] || tp?.edited_review || "";

  const getFlagCode = (): string => {
    if (config.IS_CHINA_APP) return "cn";
    const code = getNationalityCountryCode(tp.nationality);
    return code || "";
  };

  const toggleExpanded = () => {
    gtagEventEmitter({
      action: expanded
        ? "trustpilot_review_subsection_read_less_clicked"
        : "trustpilot_review_subsection_read_more_clicked",
      category: PAGE.SEARCH,
    });
    setExpanded(!expanded);
  };

  const onKeyDown = (event: any) => {
    if (event.key === "Enter") {
      toggleExpanded();
    }
  };

  return (
    <div
      className={formatClassNames(
        styles.trustPilotCarouselItem,
        isHomePage && isDesktop && styles.trustpilotHomeItem,
        !isHomePage && styles.addPadding,
      )}
      data-testid="homepage-tp-review-carousel-item"
    >
      <div className={styles.carouselItemBorder}>
        <div className={styles.readMoreContainer}>
          <div
            ref={ref}
            className={`${styles.trustPilotCarouselReviewText} ${expanded ? styles.expanded : ""}`}
            data-testid="homepage-tp-customer-review"
          >
            {reviewText?.length > charLimit ? (
              <>
                {expanded ? (
                  <>
                    {reviewText}
                    <span
                      onClick={toggleExpanded}
                      className={styles.read}
                      role="button"
                      onKeyDown={onKeyDown}
                      tabIndex={0}
                      data-testid="Read Less"
                    >
                      &nbsp; {t("Read Less")}
                    </span>
                  </>
                ) : (
                  <>
                    {reviewText.substr(0, charLimit)}
                    .....
                    <span
                      className={styles.read}
                      onClick={toggleExpanded}
                      role="button"
                      onKeyDown={onKeyDown}
                      tabIndex={0}
                      data-testid="Read More"
                    >
                      {t("Read More")}
                    </span>
                  </>
                )}
              </>
            ) : (
              reviewText
            )}
          </div>
        </div>
        <div className={styles.trustPilotBottom}>
          <div className={styles.avatarContainer}>
            <LazyImage
              height={42}
              width={42}
              src={getRandomAvatar(tp?.user_name || "", 0)}
              className={styles.avatarImage}
              alt={`${tp?.user_name || "User"} avatar`}
            />
            {!hideFlagBadge && (
              <div className={styles.flagBadge}>
                <CountryFlag
                  countryCode={getFlagCode()}
                  width={16}
                  height={16}
                  className={styles.flagImage}
                />
              </div>
            )}
          </div>
          <div className={styles.trustPilotMeta}>
            <div className={styles.trustPilotUser} data-testid="homepage-tp-user-name">
              {tp?.user_name?.length > 13 ? tp?.user_name?.split(" ")[0] : tp?.user_name}
            </div>
            <div
              className={isHomePage ? styles.trustPilotUniHome : styles.trustPilotUni}
              data-testid="homepage-tp-university-name"
            >
              {tp?.university}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(TPCarouselItem, {
  componentName: "TPCarouselItem-SearchListDesktop-SearchPage",
  showForChina: true,
});
